// client/src/pages/usecases/CampaignValidationPage.js
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../index.css';
import '../../styles/HomePage.css';
import './usecases.css';
import { useAuth } from '../../authentication/AuthContext';
import { event } from '../../services/analyticsService';

function CampaignValidationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Track page view
    event({
      action: 'view_use_case_detail',
      category: 'engagement',
      label: 'campaign_validation'
    });
  }, []);

  const handleTryTemplate = () => {
    // Track template usage
    event({
      action: 'try_template',
      category: 'conversion',
      label: 'campaign_validation_template'
    });
    
    // Check if user is authenticated
    if (!user) {
      navigate('/login', { 
        state: { 
          from: '/research',
          message: 'Please log in to access the app and try this template'
        } 
      });
      return;
    }
    
    navigate('/research', { 
      state: { template: 'campaign-validation' } 
    });
  };

  const benefits = [
    {
      title: "Test Multiple Concepts",
      description: "Compare different campaign approaches to identify the most effective direction",
      alt: "Message testing"
    },
    {
      title: "Refine Audience Targeting",
      description: "Discover which demographics respond best to different campaign elements",
      alt: "Audience targeting"
    },
    {
      title: "Optimize Before Launch",
      description: "Identify and fix potential issues before investing in full campaign rollout",
      alt: "Campaign optimization"
    }
  ];

  const steps = [
    {
      number: 1,
      title: "Describe Your Campaign",
      description: "Input your campaign concept, key messages, creative direction, or specific ads you want to test."
    },
    {
      number: 2,
      title: "Define Your Audience",
      description: "Select demographic filters that match your campaign's target audience segments."
    },
    {
      number: 3,
      title: "Collect Simulated Feedback",
      description: "Our AI personas respond as real consumers would, providing authentic reactions to your campaign elements."
    },
    {
      number: 4,
      title: "Analyze Campaign Potential",
      description: "Review comprehensive insights on message clarity, appeal, call-to-action effectiveness, and purchase intent."
    }
  ];

  // Define case study findings data
  const findings = [
    "The <strong>\"Transform Your Fitness Journey\"</strong> concept resonated more than \"Track Everything\" or \"Fitness Intelligence\"",
    "Emotional benefits were <span class='highlight'>more compelling</span> than technical specifications",
    "The call-to-action <strong>\"Start Your Transformation\"</strong> outperformed others by <span class='highlight'>35%</span>",
    "Different age groups had significantly different responses to the visuals"
  ];

  return (
    <div className="pricing-page">
      <div className="top-hero">
        <div className="hero-grid-item">
          <h4 style={{fontWeight: '400'}}>USE CASE</h4>
          <h1 style={{fontSize: '4rem', marginBottom: "3vh", marginTop: "3vh"}}>Campaign Idea Validation</h1>
          <h3 style={{fontWeight: '400'}}>Validate marketing campaigns before launch</h3>
          
          <div className="button-container">
            <button onClick={handleTryTemplate} className="standout-button button">Try This Template</button>
          </div>
        </div>
      </div>

      <div className="hero" style={{backgroundColor: 'var(--secondary-color', padding: 0, marginTop: '5vh'}}> 
        <div className="content-grid-2" style={{margin: '6vh 10vw', gap: '7rem', justifyContent: 'center'}}>
          <div>
            <h2 style={{fontSize: '2.5rem', fontWeight: '500', position: 'relative', paddingLeft: '2rem'}}>
              <span style={{
                position: 'absolute',
                left: '-1rem',
                top: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
              <i>Stop guessing what works. Know before you spend.</i>
              <span style={{
                position: 'absolute',
                right: '-2rem',
                bottom: '-2rem',
                fontSize: '6rem',
                fontFamily: 'serif',
                color: 'var(--tertiary-color)',
                opacity: '0.7'
              }}>"</span>
            </h2>
          </div>
          <div style={{textAlign: 'center'}}>
            <p style={{lineHeight: '1.7', letterSpacing: '0.05em', fontWeight: '500', boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)', borderRadius: '10px', padding: '3vh 2vw', backgroundColor: 'var(--background-color)'}}>
              InstantFlows helps marketing teams <span style={{color: 'var(--tertiary-color)'}}>test campaign ideas</span>, messages, and concepts before launch.
              <br/> <br/>
              Ensuring your efforts connect effectively with your audience and deliver maximum ROI.
            </p>
          </div>
        </div>
      </div>

      <div className="benefits-section">
        <h2 className="benefits-title">Key Benefits</h2>
        <div className="benefits-container">
          {benefits.map((benefit, index) => (
            <div key={index} className="benefit-card">
              <div className="benefit-header">
                <div className="icon-container">
                  <img src="/InstantFlowsicon_wb.png" alt={benefit.alt} className="benefit-icon" />
                </div>
                <h3 className="benefit-title">{benefit.title}</h3>
              </div>
              <p className="benefit-description">{benefit.description}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="hero hero-3">
        <h2>How It Works</h2>
        <div className="content-grid-4" style={{maxWidth: "1200px", margin: "0 4rem", gap: "1.5rem"}}>
          {steps.map((step, index) => (
            <div key={index} className="card" style={{padding: "1.5rem", transition: "transform 0.3s ease", marginBottom: '1rem'}}>
              <div style={{display: "flex", alignItems: "center", marginBottom: "0.5rem"}}>
                <div style={{
                  background: "var(--tertiary-color)", 
                  color: "white", 
                  width: "32px", 
                  height: "32px", 
                  borderRadius: "50%", 
                  display: "flex", 
                  alignItems: "center", 
                  justifyContent: "center", 
                  fontWeight: "bold",
                  marginRight: "0.8rem"
                }}>{step.number}</div>
                <h3 style={{margin: "0", fontWeight: '500'}}>{step.title}</h3>
              </div>
              <p style={{margin: "0", textAlign: 'left'}}>{step.description}</p>
            </div>
          ))}
        </div>
        <div className="button-container" style={{maxHeight: "none", paddingBottom: "1rem"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this Template</button>
        </div>
      </div>

      <div className="hero hero-1" style={{backgroundColor: 'var(--secondary-color)', padding: "1rem", paddingBottom:'2rem', margin: '2rem auto', marginBottom: '0'}}>
        <h2 className="section-heading">Case Study</h2>
        <div className="case-study-container">
          <div className="section-content" style={{boxShadow: 'none'}}>
            <h3 className="case-study-title">FitTech's Product Launch</h3>
            <p className="case-study-text">
              FitTech, a fitness wearables company, used InstantFlows to test three different campaign approaches for their new product launch.
              <br/><br/>
              Their findings:
            </p>
            <div className="findings-list">
              {findings.map((finding, index) => (
                <p key={index} className="finding-item">
                  <span className="bullet-point">•</span>
                  <span dangerouslySetInnerHTML={{ __html: finding }}></span>
                </p>
              ))}
            </div>
            <p className="case-study-conclusion">
              These insights helped FitTech refine their campaign message and target specific demographics with tailored creative, resulting in their most successful product launch to date.
            </p>
          </div>
        </div>
      </div>

      <div className="hero hero-2" style={{backgroundColor: 'var(--tertiary-color)', paddingTop: '4rem'}}>
        <div className="textbox" style={{width: "70%", background: "transparent", boxShadow: "none", color: "white"}}>
          <blockquote style={{fontSize: "1.2rem", fontStyle: "italic", lineHeight: '2.2rem'}}>
            "InstantFlows helped us identify a critical flaw in our campaign messaging before we spent our quarterly budget. What we thought was clever wordplay was actually confusing to our audience. We pivoted, simplified our message, and saw conversion rates double compared to previous campaigns."
          </blockquote>
          <div style={{display: "flex", justifyContent: "right", alignItems: "center", marginTop: "2rem"}}>
            <div style={{textAlign: "right", marginLeft: "1rem"}}>
              <p style={{margin: 0, fontWeight: "600"}}>Alex Rivera</p>
              <p style={{margin: 0, fontSize: "0.9rem"}}>Marketing Director, FitTech</p>
            </div>
          </div>
        </div>
      </div>

      <div className="cta-section">
        <h2 className="cta-title">Ready to Validate Your Campaign?</h2>
        <p className="cta-description">Test your marketing concepts before committing your budget.</p>
        <div className="button-container" style={{maxHeight: "none"}}>
          <button onClick={handleTryTemplate} className="standout-button button">Try this template</button>
        </div>
      </div>

      <div className="section-divider"></div>

      <div className="examples-section">
        <h2 className="examples-title" style={{marginTop: '0'}}>Example Researches</h2>
        <div className="examples-grid">
          <div 
            className="example-card" 
            onClick={() => navigate('/exampleresearches', { state: { filter: 'startup-validation', example: 'example1' } })}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Product Market Fit Analysis" 
            />
            <h3 className="example-title">Product Market Fit Analysis</h3>
            <p className="example-description">
              See how a tech startup validated their app concept by testing user intent and feature prioritization.
            </p>
            <button className="button example-button">View Example</button>
          </div>
          
          <div 
            className="example-card" 
            onClick={() => navigate('/exampleresearches', { state: { filter: 'startup-validation', example: 'example2' } })}
          >
            <img 
              className="example-image" 
              src="/Example-1.png" 
              alt="Pricing Strategy Test" 
            />
            <h3 className="example-title">Pricing Strategy Test</h3>
            <p className="example-description">
              Explore how a SaaS company determined their optimal pricing model and subscription tiers.
            </p>
            <button className="button example-button">View Example</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CampaignValidationPage;